import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Building,
  Calendar,
  Clock,
  Target,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Share,
  Settings,
  RefreshCw,
  Filter,
  Search,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  MoreHorizontal,
  Bookmark,
  Star,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Zap,
  Shield,
  Lock,
  Key,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  Copy,
  Globe,
  Receipt,
  CreditCard,
  Calculator,
  Users2,
  AlertTriangle,
  CheckSquare as CheckSquareIcon,
  FileText,
  Info,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import { useToast } from '../common/Toast';

const Dashboards = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalAssets: 0,
      totalContracts: 0,
      totalValue: 0,
      activeProjects: 0,
      assetGrowth: 0,
      contractGrowth: 0,
      valueGrowth: 0,
      projectGrowth: 0,
    },
    assets: {
      byCategory: [],
      byStatus: [],
      byLocation: [],
    },
    contracts: {
      byType: [],
      byStatus: [],
      byValue: [],
    },
    financial: {
      revenue: { current: 0, previous: 0, growth: 0 },
      expenses: { current: 0, previous: 0, growth: 0 },
      profit: { current: 0, previous: 0, growth: 0 },
      cashFlow: { current: 0, previous: 0, growth: 0 },
    },
  });

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, [selectedTimeRange, selectedDepartment]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        timeRange: selectedTimeRange,
        department: selectedDepartment,
      });

      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.DASHBOARD}/insights?${params}`,
        {
          headers: API_CONFIG.HEADERS,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error loading insights data:', errorData);
        showError(errorData.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error loading insights data:', err);
      showError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh button click
  const handleRefresh = () => {
    loadDashboardData();
  };

  // Handle export button click
  const handleExport = () => {
    // Simulate export functionality
    console.log('Exporting dashboard data...');
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/business-intelligence-dashboards';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh dashboard data',
      disabled: loading,
    },
    {
      label: 'Export',
      icon: <Download size={18} />,
      onClick: handleExport,
      variant: 'outline',
      tooltip: 'Export dashboard data',
    },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'assets', label: 'Asset Analytics' },
    { id: 'contracts', label: 'Contract Analytics' },
    { id: 'financial', label: 'Financial Analytics' },
    { id: 'custom', label: 'Custom Dashboards' },
  ];

  const timeRanges = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
  ];

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'it', label: 'IT' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' },
    { value: 'hr', label: 'Human Resources' },
  ];

  const StatCard = ({
    title,
    value,
    growth,
    icon: Icon,
    iconBg = '#D6EFFF',
    iconLine = '#2D7DD2',
    format = 'number',
  }) => {
    const formatValue = (val) => {
      if (format === 'currency') {
        return `$${(val / 1000000).toFixed(1)}M`;
      }
      return val.toLocaleString();
    };

    const isPositive = growth >= 0;
    const trendText = `${isPositive ? '+' : ''}${growth}%`;

    return (
      <div className="relative border border-[var(--border-color)] bg-white">
        {/* Accent line – top */}
        <div className="h-1 w-full bg-[var(--primary-color)]" />

        {/* Content */}
        <div className="relative flex items-center gap-3 p-4">
          {/* Trend – top right */}
          {growth !== undefined && (
            <div
              className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${
                isPositive ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {isPositive ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )}
              {trendText}
            </div>
          )}

          {/* Icon */}
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center"
            style={{ background: iconBg }}
          >
            <Icon size={20} style={{ color: iconLine }} />
          </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              {formatValue(value)}
            </div>
            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
              {title}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ChartCard = ({ title, data, type = 'pie' }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    return (
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e5e7eb',
        }}
      >
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 20px 0',
          }}
        >
          {title}
        </h3>

        {type === 'pie' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            {/* Pie Chart Visualization */}
            <div
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: `conic-gradient(${data
                  .map(
                    (item, index) =>
                      `${item.color} 0deg ${(item.value / total) * 360}deg`
                  )
                  .join(', ')})`,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#1f2937',
                  }}
                >
                  {total}
                </span>
              </div>
            </div>

            {/* Legend */}
            <div style={{ flex: 1 }}>
              {data.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        backgroundColor: item.color,
                      }}
                    />
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '500',
                      }}
                    >
                      {item.name}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {item.value}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        minWidth: '40px',
                        textAlign: 'right',
                      }}
                    >
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            {data.map((item, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#374151',
                      fontWeight: '500',
                    }}
                  >
                    {item.name}
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1f2937',
                    }}
                  >
                    {item.value}
                  </span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${item.percentage}%`,
                      height: '100%',
                      backgroundColor: item.color,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Business Intelligence Dashboards"
        description="Comprehensive analytics and insights for assets, contracts, and financial performance"
        actions={headerActions}
      />
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6 mt-8 mr-6 ml-6">
        <StatCard
          title="Total Assets"
          value={dashboardData.overview.totalAssets}
          growth={dashboardData.overview.assetGrowth}
          icon={Building}
          iconBg="#DFF8F0"
          iconLine="#22A07A"
        />
        <StatCard
          title="Total Contracts"
          value={dashboardData.overview.totalContracts}
          growth={dashboardData.overview.contractGrowth}
          icon={FileText}
          iconBg="#F3E7FF"
          iconLine="#8A3FC5"
        />
        <StatCard
          title="Total Value"
          value={dashboardData.overview.totalValue}
          growth={dashboardData.overview.valueGrowth}
          icon={DollarSign}
          iconBg="#E6F7EE"
          iconLine="#2CA55C"
          format="currency"
        />
        <StatCard
          title="Active Projects"
          value={dashboardData.overview.activeProjects}
          growth={dashboardData.overview.projectGrowth}
          icon={Target}
          iconBg="#EBDFFF"
          iconLine="#A45BDE"
        />
      </div>
      <div className="page-container">
        {/* Controls */}
        <div className="mb-4 flex w-full justify-end">
          <div className="flex flex-wrap items-center gap-6">
            {/* Time Range */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Time Range:
              </label>
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-800 focus:border-gray-400 focus:outline-none"
              >
                {timeRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Department:
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-800 focus:border-gray-400 focus:outline-none"
              >
                {departments.map((dept) => (
                  <option key={dept.value} value={dept.value}>
                    {dept.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div className="project-tabs" style={{ marginBottom: '32px' }}>
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">
                  {tab.id === 'overview' && <BarChart3 size={18} />}
                  {tab.id === 'assets' && <Building size={18} />}
                  {tab.id === 'contracts' && <FileText size={18} />}
                  {tab.id === 'financial' && <DollarSign size={18} />}
                  {tab.id === 'custom' && <Settings size={18} />}
                </div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Charts Grid */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-6">
              <ChartCard
                title="Assets by Category"
                data={dashboardData.assets.byCategory}
                type="pie"
              />

              <ChartCard
                title="Assets by Status"
                data={dashboardData.assets.byStatus}
                type="bar"
              />

              <ChartCard
                title="Contracts by Type"
                data={dashboardData.contracts.byType}
                type="pie"
              />

              <ChartCard
                title="Contracts by Status"
                data={dashboardData.contracts.byStatus}
                type="bar"
              />
            </div>
          </div>
        )}

        {/* Asset Analytics Tab */}
        {activeTab === 'assets' && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '24px',
              }}
            >
              <ChartCard
                title="Assets by Category"
                data={dashboardData.assets.byCategory}
                type="pie"
              />
              <ChartCard
                title="Assets by Status"
                data={dashboardData.assets.byStatus}
                type="bar"
              />
              <ChartCard
                title="Assets by Location"
                data={dashboardData.assets.byLocation}
                type="pie"
              />
            </div>
          </div>
        )}

        {/* Contract Analytics Tab */}
        {activeTab === 'contracts' && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '24px',
              }}
            >
              <ChartCard
                title="Contracts by Type"
                data={dashboardData.contracts.byType}
                type="pie"
              />
              <ChartCard
                title="Contracts by Status"
                data={dashboardData.contracts.byStatus}
                type="bar"
              />
              <ChartCard
                title="Contracts by Value"
                data={dashboardData.contracts.byValue}
                type="pie"
              />
            </div>
          </div>
        )}

        {/* Financial Analytics Tab */}
        {activeTab === 'financial' && (
          <div>
            {/* Financial Metrics */}
            <div className="page-stats-grid" style={{ marginBottom: '32px' }}>
              <StatCard
                title="Revenue"
                value={dashboardData.financial.revenue.current}
                growth={dashboardData.financial.revenue.growth}
                icon={TrendingUp}
                color="#10b981"
                format="currency"
              />
              <StatCard
                title="Expenses"
                value={dashboardData.financial.expenses.current}
                growth={dashboardData.financial.expenses.growth}
                icon={TrendingDown}
                color="#ef4444"
                format="currency"
              />
              <StatCard
                title="Profit"
                value={dashboardData.financial.profit.current}
                growth={dashboardData.financial.profit.growth}
                icon={DollarSign}
                color="#f59e0b"
                format="currency"
              />
              <StatCard
                title="Cash Flow"
                value={dashboardData.financial.cashFlow.current}
                growth={dashboardData.financial.cashFlow.growth}
                icon={Activity}
                color="#8b5cf6"
                format="currency"
              />
            </div>
          </div>
        )}

        {/* Custom Dashboards Tab */}
        {activeTab === 'custom' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto',
              }}
            >
              <Settings size={32} style={{ color: '#6b7280' }} />
            </div>
            <h3
              style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 12px 0',
              }}
            >
              Custom Dashboards
            </h3>
            <p
              style={{
                fontSize: '16px',
                color: '#6b7280',
                margin: '0 0 24px 0',
                maxWidth: '500px',
                margin: '0 auto 24px auto',
              }}
            >
              Create personalized dashboards with custom metrics, charts, and
              KPIs tailored to your specific needs.
            </p>
            <button
              className="btn-cam"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto',
              }}
            >
              <Plus size={20} />
              Create Custom Dashboard
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboards;
